import { SecondaryButton, Table, useModal } from '@shape-ds/components';

import PermissionedButton from 'components/PermissionedButton/PermissionedButton';
import { TableFilters } from 'components/TableFilters';
import useFilters from 'hooks/useFilters/useFilters';
import {
  CaseAlarmsFilters,
  RelatedAlarmsResponse,
  useCaseRelatedAlarms,
} from 'queries/cases/queries';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Modal.style.scss';
import { updateCaseEventItemData } from './helper/helper';
import { groupEvents } from 'pages/Asset/tabs/Events/utils/EventGrouping';
import { useRelatedAlarmModalColumns } from './hooks/useRelatedAlarmModalColumns';
import { EventItemExtended } from 'queries/events/types';
import { useDatesHelper } from '@shape/lighthouse-components';
import { useQueryClient } from '@tanstack/react-query';
import { NoData } from '../NoData/NoData';

type AlarmTableProps = {
  data: {
    id?: string;
    asset?: { id: string; name: string };
  };
  onSave: (newAlarms: EventItemExtended[]) => void;
  isDisabled: boolean;
};

export const alarmFieldType = {
  is_automatic: 'is_automatic',
  is_assigned_to_case: 'is_assigned_to_case',
} as const;

export type ALARM_ASSIGNED_TYPE = (typeof alarmFieldType)[keyof typeof alarmFieldType];

interface HandleRelatedAlarmChange {
  id: string;
  changeField: ALARM_ASSIGNED_TYPE;
  value: boolean;
}

const DEFAULT_PERIOD_IN_DAYS = '7';

export const RelatedAlarmModalTable = ({ data, onSave, isDisabled }: AlarmTableProps) => {
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const [isDirty, setIsDirty] = useState(false);

  const { buildDateRange } = useDatesHelper();
  const defaultPeriod = buildDateRange(DEFAULT_PERIOD_IN_DAYS);
  const [queryFilters, setQueryFilters] = useState<CaseAlarmsFilters>({
    start_date: defaultPeriod.startDate,
    asset_id: data.asset?.id,
    id: data.id,
  });

  const {
    alarms: alarmsData,
    asset_alarms,
    isSuccess,
    isFetching,
  } = useCaseRelatedAlarms(queryFilters);

  const relatedAlarmsList = useMemo(() => {
    if (!alarmsData || !asset_alarms) return [];
    return [
      ...alarmsData,
      ...asset_alarms.filter(
        (assetAlarm) => !alarmsData.some((alarm) => alarm.id === assetAlarm.id),
      ),
    ];
  }, [alarmsData, asset_alarms]);

  const [filteredData, setFilteredData] = useState<EventItemExtended[]>(relatedAlarmsList ?? []);

  useEffect(() => {
    if (isSuccess) {
      setFilteredData(relatedAlarmsList);
    }
  }, [isSuccess]);

  const groupedData = useMemo(() => groupEvents(filteredData), [filteredData]);

  const handleChange = ({ changeField, id, value }: HandleRelatedAlarmChange) => {
    setIsDirty(true);

    const isClone = id.includes('-CLONE');
    const idsToChange = isClone
      ? groupedData
          .find((alarm) => alarm.id === id)
          ?.children?.map((childrenAlarm) => childrenAlarm.id) ?? [id]
      : [id];

    const newData = updateCaseEventItemData({
      actualData: filteredData,
      changeField,
      value,
      ids: idsToChange,
    });

    setFilteredData(newData);
  };

  const { onChange } = useFilters<EventItemExtended>({
    data: relatedAlarmsList,
    onChangeFilters: (newFilteredData) => {
      if (!(newFilteredData.length === 0)) {
        setFilteredData(newFilteredData);
      }
    },
    multipleFieldSearch: ['description'],
  });

  const { columns } = useRelatedAlarmModalColumns({
    handleChange,
    disabled: isDisabled,
  });

  return (
    <section className="case-related-alarms-modal" data-testid="related-alarms-modal">
      <TableFilters
        activeQueryFilters={queryFilters}
        onChangeFilters={onChange}
        onChangeQueryFilters={setQueryFilters}
        categoryFilter="conditionDeviations"
        hasVesselFilter={false}
        hasDateFilter
        hasSwitchFilter={false}
      />

      {groupedData.length > 0 ? (
        <Table<EventItemExtended>
          className="case-related-alarms-add"
          data={groupedData}
          columns={columns}
          style={{
            height: 230,
          }}
        />
      ) : (
        <NoData i18nKey="case:no_data.related_alarms_modal" icon={null} isLoading={isFetching} />
      )}

      <div className="alarm-buttons-wrapper">
        <SecondaryButton
          onClick={() => {
            closeModal();
          }}
        >
          {t('case:common.cancel')}
        </SecondaryButton>
        <PermissionedButton
          disabled={!isDirty}
          onClick={() => {
            queryClient.setQueryData(
              ['GET_CASE_ALARMS', queryFilters],
              (oldData?: RelatedAlarmsResponse) => {
                if (!oldData) return oldData;
                return {
                  ...oldData,
                  alarms: filteredData,
                };
              },
            );
            onSave(filteredData.filter((alarm) => alarm.is_assigned_to_case));
            closeModal();
          }}
        >
          {t('case:common.save')}
        </PermissionedButton>
      </div>
    </section>
  );
};
